import { Link, useParams } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { comics } from '@/mocks/comics';
import { comicCoverImages } from '@/mocks/coverImages';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function CategoryDetail() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryComics = comics.filter((c) => c.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-foreground-500 mb-4">请检查分类链接是否正确</p>
            <Link to="/category" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">
              返回分类列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${category.name}漫画-韩漫漫画,免费${category.name}漫画在线阅读`}
        description={`韩漫漫画${category.name}分类，共收录${category.count}部优质${category.name}韩国漫画。${category.description}。免费在线阅读，每日更新。`}
        keywords={`${category.name}漫画,韩漫${category.name},免费${category.name}漫画,韩国${category.name}漫画`}
        canonicalUrl={`/category/${category.slug}`}
      />
      <SchemaOrg type="WebPage" data={{
        name: `${category.name}漫画 - 韩漫漫画`,
        description: `${category.name}韩国漫画分类，共${category.count}部作品`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://netkqn.com/' },
            { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'http://netkqn.com/category' },
            { '@type': 'ListItem', position: 3, name: `${category.name}漫画`, item: `http://netkqn.com/category/${category.slug}` },
          ],
        },
      }} />

      <Navbar />
      <main className="pt-20">
        <section className="py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="mx-auto max-w-7xl">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <Link to="/category" className="hover:text-primary-500 transition-colors cursor-pointer">漫画分类</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-800 font-medium">{category.name}漫画</span>
              </nav>

              <div className="flex items-start gap-4 mb-10">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-500 flex-shrink-0">
                  <i className={`${category.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                    {category.name}漫画
                  </h1>
                  <p className="text-foreground-500 text-sm md:text-base">{category.description}</p>
                  <p className="text-foreground-400 text-xs mt-1">共 {category.count} 部作品</p>
                </div>
              </div>

              {categoryComics.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                  {categoryComics.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/comic/${comic.slug}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative overflow-hidden rounded-xl bg-background-100 aspect-[3/4] mb-3">
                        <img
                          src={comicCoverImages[comic.slug]}
                          alt={comic.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                          loading="lazy"
                          title={comic.title}
                        />
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs">
                          {comic.rating}
                        </div>
                      </div>
                      <h3 className="text-foreground-950 text-sm font-medium truncate group-hover:text-primary-500 transition-colors">
                        {comic.title}
                      </h3>
                      <p className="text-foreground-500 text-xs mt-0.5">{comic.author}</p>
                      <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 text-xs">
                        {comic.status}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <p className="text-foreground-500">该分类下暂无漫画作品</p>
                </div>
              )}

              {categoryComics.length > 0 && (
                <div className="mt-10 text-center">
                  <Link
                    to="/category"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 font-medium text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i> 返回全部分类
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}